import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { isEmpty, get, filter } from 'lodash';
import AddressFormatter from '@shopify/address';

import { injectIntl } from 'react-intl';

import { fetchBooking } from '../../app-main/actions/booking';
import ReviewOfferDetailsMain from '../../app-bookings-custom/review-offer-details-main';
import { PageSpinner } from 'shared-components/decorators/spinner';
import WithdrawOfferModal from '../../app-bookings-custom/modals/withdraw-offer-modal';
import {
  acceptOffer,
  acceptHostUpdate,
  declineOffer,
  declineHostUpdate,
  fetchContractsSnapshot
} from '../../app-main/actions/contracts';
import { checkCCStateInterval, getShowBillingForm } from '../../app-bookings-request/forms/bookingUtil';
import ProcessingPage from '../../app-bookings-request/ProcessingPage';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import { fetchAllFeeProfiles } from '../actions/feeProfile';
import { fetchEnvSpaces } from '../actions/env';
import { formValueSelector } from 'redux-form';
import { REVIEW_OFFER_FORM } from '../components/reduxForms/constants/formNames';
import { fetchSpaceCountryStatus } from '../actions/space';

class ReviewOfferDetailsContainer extends Component {
  constructor (props) {
    super(props);

    this.state = {
      whichModal: '',
      paymentErrorMsg: '',
      ccProcessing: false
    };

    this.toggleModal = this.toggleModal.bind(this);
    this.handleSeeCurrentDetailsClick = this.handleSeeCurrentDetailsClick.bind(this);
    this.handleWithdrawOfferClick = this.handleWithdrawOfferClick.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.fetchFeeProfiles = this.fetchFeeProfiles.bind(this);
    this.redirectToInboxDetails = this.redirectToInboxDetails.bind(this);
    this.acceptOfferAction = this.acceptOfferAction.bind(this);
    this.declineOfferAction = this.declineOfferAction.bind(this);
    this.withdrawCallback = this.withdrawCallback.bind(this);
    this.checkCCState = this.checkCCState.bind(this);
  }

  componentDidMount () {
    const { profile } = this.props;
    if (profile.hasFetched) {
      this.fetchData();
    }

    const locale = SharedIntlProviderHOC.getLocale();
    const localeCode = locale.split('-')[0];
    // localized list of all countries

    const localAddressFormatter = new AddressFormatter(localeCode);
    const globalAddressFormatter = new AddressFormatter('en');
    Promise.all([
      localAddressFormatter.getCountries(),
      globalAddressFormatter.getCountries()
    ]).then((values) => {
      this.setState({
        allCountries: values[0],
        globalCountries: values[1]
      });
    }).catch((err) => {
      if ('function' === typeof Bugsnag) {
        Bugsnag.notify('Shopify address lib error', err);
      }
    });
  }

  componentWillReceiveProps (nextProps) {
    const { accessToken, profile, booking } = nextProps;
    const isHost = profile.SSOid === get(booking, 'booking.host.ssoid');
    const isGuest = profile.SSOid === get(booking, 'booking.guest.ssoid');
    const urlPrefix = SharedIntlProviderHOC.getUrlPrefix();

    // if user logs out, redirect to sign in
    if (!accessToken) {
      window.location.href = `${urlPrefix ? `/${urlPrefix}` : ''}/`;
    }

    // only booking hosts or guests can access form
    if (!isEmpty(booking.booking) && (!!profile.SSOid && !isGuest && !isHost)) {
      window.location.href = '/inbox';
    }
  }

  componentDidUpdate (prevProps) {
    const { accessToken, booking, profile } = this.props;
    const isFetching = booking.isFetching;

    if (isEmpty(booking.booking) && !isFetching && accessToken && profile.hasFetched) {
      this.fetchData();
    }

    if (!prevProps.profile.hasFetched && profile.hasFetched) {
      this.fetchFeeProfiles();
    }
  }

  fetchFeeProfiles () {
    const { dispatch, accessToken, profile } = this.props;

    if (accessToken && profile.hasFetched) {
      dispatch(fetchSpaceCountryStatus(profile.SSOid, accessToken));
      dispatch(fetchAllFeeProfiles(accessToken, profile.SSOid)).then((data) => {
        const billingAddress = filter(data, { type: 'BILLING' });
        const showBillingForm = getShowBillingForm(billingAddress);
        this.setState({
          showBillingForm
        });
      });
    }
  }

  fetchData () {
    const {
      dispatch,
      routeParams,
      accessToken,
      profile
    } = this.props;
    const bookingId = get(routeParams, 'bookingID') || this.props.bookingId;

    if (bookingId && accessToken) {
      dispatch(fetchBooking(bookingId, accessToken));
      dispatch(fetchEnvSpaces());
      dispatch(fetchContractsSnapshot(bookingId, accessToken));
    }

    if (profile.hasFetched) {
      this.fetchFeeProfiles();
    }
  }

  toggleModal (whichModal, paymentErrorMsg) {
    this.setState({ whichModal, paymentErrorMsg });
  }

  handleSeeCurrentDetailsClick () {
    const bookingId = get(this.props.routeParams, 'bookingID');
    this.context.router.push({ pathname: `/inbox/${bookingId}/current-details` });
  }

  withdrawCallback () {
    const { routeParams, iosWebView, finalCb, closeIOSModal } = this.props;

    if (iosWebView) {
      closeIOSModal();
      return finalCb();
    }

    const bookingID = get(routeParams, 'bookingID') || this.props.bookingId;
    this.context.router.push({ pathname: `/inbox/${bookingID}` });
  }

  handleWithdrawOfferClick (isHostUpdate) {
    const { routeParams } = this.props;
    const bookingID = get(routeParams, 'bookingID') || this.props.bookingId;

    ReactDOM.render(
      <SharedIntlProviderHOC>
        <WithdrawOfferModal
          offerAction="WITHDRAW_OFFER"
          bookingId={ bookingID }
          callback={this.withdrawCallback}
          bookingState={isHostUpdate ? 'HOST_UPDATED_REQUESTED' : ''}
        />
      </SharedIntlProviderHOC>, document.getElementById('modal-container')
    );
  }

  acceptOfferAction (payload, submitCb, isUpdate, billingAddressPayload) {
    const { dispatch, booking, accessToken } = this.props;
    const bookingId = get(booking, 'booking._id');

    if (isUpdate) {
      return dispatch(acceptHostUpdate(payload, bookingId, accessToken, submitCb, billingAddressPayload));
    }

    return dispatch(acceptOffer(payload, bookingId, accessToken, submitCb, billingAddressPayload));
  }

  declineOfferAction (payload, submitCb, isUpdate) {
    const { dispatch, booking, accessToken } = this.props;
    const bookingId = get(booking, 'booking._id');

    if (isUpdate) {
      return dispatch(declineHostUpdate(payload, bookingId, accessToken, submitCb));
    }

    return dispatch(declineOffer(payload, bookingId, accessToken, submitCb));
  }

  redirectToInboxDetails () {
    const { closeIOSModal, finalCb, iosWebView, booking } = this.props;
    const bookingId = get(this.props, 'booking.booking._id');

    if (iosWebView) {
      closeIOSModal();
      return finalCb();
    }

    this.context.router.push({ pathname: `/inbox/${bookingId}` });
  }

  checkCCState () {
    const { accessToken, booking, iosWebView, closeIOSModal } = this.props;
    const bookingId = get(booking, 'booking._id');

    this.setState({
      ccProcessing: true
    }, () => {
      checkCCStateInterval(bookingId, accessToken, this.redirectToInboxDetails);
    });
  }

  render () {
    const {
      booking,
      profile,
      contract,
      iosWebView,
      closeIOSModal,
      scrollTopIOS,
      dispatch,
      billingCountryValue,
      billingStateValue,
      billingCityValue,
      billingStreetValue,
      billingUnitValue,
      billingZipValue
    } = this.props;

    const { whichModal, paymentErrorMsg,
      genericError, allCountries, globalCountries, showBillingForm } = this.state;

    const isHost = profile.SSOid === get(booking, 'booking.host.ssoid');
    const isGuest = profile.SSOid === get(booking, 'booking.guest.ssoid');
    const bookingId = get(booking, 'booking._id');

    if (this.state.ccProcessing) {
      return <ProcessingPage />
    }

    return (
      <div className="ReviewOfferDetailsContainer">
        {
          (isEmpty(contract.data) || isEmpty(booking.booking) || isEmpty(profile.data))
              ? <PageSpinner additionalStyle="gray" />
              : <ReviewOfferDetailsMain
                  {...this.props}
                  booking={booking}
                  handleWithdrawOfferClick={this.handleWithdrawOfferClick}
                  redirectToInboxDetails={this.redirectToInboxDetails}
                  isHost={isHost}
                  isGuest={isGuest}
                  acceptOfferAction={this.acceptOfferAction}
                  declineOfferAction={this.declineOfferAction}
                  toggleModal={this.toggleModal}
                  whichModal={whichModal}
                  paymentErrorMsg={paymentErrorMsg}
                  bookingId={bookingId}
                  scrollTopIOS={scrollTopIOS}
                  iosWebView={iosWebView}
                  closeIOSModal={closeIOSModal}
                  checkCCState={this.checkCCState}
                  genericError={genericError}
                  allCountries={allCountries}
                  globalCountries={globalCountries}
                  profile={profile}
                  showBillingForm={showBillingForm}
                  billingCountryValue={billingCountryValue}
                  billingStateValue={billingStateValue}
                  billingCityValue={billingCityValue}
                  billingStreetValue={billingStreetValue}
                  billingUnitValue={billingUnitValue}
                  billingZipValue={billingZipValue}
              />
        }
      </div>
    );
  }
}

const selector = formValueSelector(REVIEW_OFFER_FORM);

function mapStateToProps (state) {
  const { auth, profile, booking, contract, feeProfiles, spaces, space } = state;
  const { accessToken } = auth;
  const guestSpaceCountry = get(space, 'data.countryStatus.country', '');
  const billingCountryValue = selector(state, 'billing_country');
  const billingStateValue = selector(state, 'billing_state');
  const billingCityValue = selector(state, 'billing_city');
  const billingStreetValue = selector(state, 'billing_street');
  const billingUnitValue = selector(state, 'billing_unit');
  const billingZipValue = selector(state, 'billing_zip');
  const isSubmitting = contract.isPosting;
  const contracts = get(contract, 'contracts');

  return {
    accessToken,
    profile,
    booking,
    isSubmitting,
    contracts,
    contract,
    feeProfiles,
    spaces,
    billingCountryValue,
    billingStateValue,
    billingCityValue,
    billingStreetValue,
    billingUnitValue,
    billingZipValue,
    guestSpaceCountry
  };
}

ReviewOfferDetailsContainer.propTypes = {
  booking: PropTypes.object,
  accessToken: PropTypes.string,
  profile: PropTypes.object,
  dispatch: PropTypes.func,
  routeParams: PropTypes.object,
  closeIOSModal: PropTypes.func,
  contract: PropTypes.object,
  iosWebView: PropTypes.bool,
  bookingId: PropTypes.string,
  scrollTopIOS: PropTypes.func,
  finalCb: PropTypes.func,
  ssoId: PropTypes.string
};

ReviewOfferDetailsContainer.contextTypes = {
  router: PropTypes.object
};

export default injectIntl(connect(mapStateToProps)(ReviewOfferDetailsContainer));
